<template>
  <div v-if="visible" class="fixed inset-0 flex items-center justify-center z-50">
    <div class="fixed inset-0 bg-black opacity-50" @click="close"></div>
    <div class="bg-white dark:bg-[#0c1427] p-4 sm:p-6 rounded-lg shadow-lg w-full max-w-md mx-4 z-10 relative">
      <div class="flex justify-between items-center mb-4">
        <h3 class="text-lg font-medium text-black dark:text-white">{{ isEditMode ? directT('settings.leadsSettings.customFields.editField') : directT('settings.leadsSettings.customFields.addField') }}</h3>
        <button 
          @click="close" 
          class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-2 -m-2"
        >
          <i class="material-symbols-outlined text-xl">close</i>
        </button>
      </div>
      
      <form @submit.prevent="saveField" class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{{ directT('settings.leadsSettings.customFields.fieldLabel') }}</label>
          <input 
            type="text" 
            v-model="fieldData.label" 
            required
            class="bg-gray-50 border border-gray-200 rounded-md w-full px-3 py-2.5 text-black dark:bg-[#15203c] dark:text-white dark:border-[#172036]"
            :placeholder="directT('settings.leadsSettings.customFields.fieldLabelPlaceholder')"
            @blur="generateFieldKey"
          />
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{{ directT('settings.leadsSettings.customFields.fieldType') }}</label>
          <select 
            v-model="fieldData.type" 
            required
            class="bg-gray-50 border border-gray-200 rounded-md w-full px-3 py-2.5 text-black dark:bg-[#15203c] dark:text-white dark:border-[#172036]"
          >
            <option value="text">{{ directT('settings.leadsSettings.customFields.typeText') }}</option>
            <option value="number">{{ directT('settings.leadsSettings.customFields.typeNumber') }}</option>
            <option value="date">{{ directT('settings.leadsSettings.customFields.typeDate') }}</option>
            <option value="email">{{ directT('settings.leadsSettings.customFields.typeEmail') }}</option>
            <option value="phone">{{ directT('settings.leadsSettings.customFields.typePhone') }}</option>
            <option value="select">{{ directT('settings.leadsSettings.customFields.typeSelect') }}</option>
            <option value="checkbox">{{ directT('settings.leadsSettings.customFields.typeCheckbox') }}</option>
          </select>
        </div>
        
        <!-- Options for dropdown field -->
        <div v-if="fieldData.type === 'select'">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {{ directT('settings.leadsSettings.customFields.dropdownOptions') }}
            <span class="text-xs font-normal text-gray-500">{{ directT('settings.leadsSettings.customFields.onePerLine') }}</span>
          </label>
          <textarea
            v-model="optionsText"
            rows="4"
            class="bg-gray-50 border border-gray-200 rounded-md w-full px-3 py-2.5 text-black dark:bg-[#15203c] dark:text-white dark:border-[#172036]"
            :placeholder="directT('settings.leadsSettings.customFields.optionsPlaceholder')"
          ></textarea>
        </div>
        
        <div class="flex items-center">
          <input 
            type="checkbox" 
            id="required-field" 
            v-model="fieldData.required" 
            class="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 dark:bg-[#15203c] dark:border-[#172036]"
          />
          <label for="required-field" class="ml-2 text-sm text-gray-700 dark:text-gray-300">
            {{ directT('settings.leadsSettings.customFields.requiredField') }}
          </label>
        </div>
        
        <div class="flex justify-end gap-2 pt-2">
          <button 
            type="button" 
            @click="close"
            class="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-md transition-all dark:bg-[#172036] dark:text-gray-200 dark:hover:bg-[#1e2746]"
          >
            {{ directT('settings.leadsSettings.delete.cancel') }}
          </button>
          <button 
            type="submit" 
            class="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-md transition-all"
          >
            {{ directT('settings.leadsSettings.saveChanges') }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, computed, PropType } from "vue";
import { CustomField } from "./CustomFieldsPanel.vue";
import { useI18n } from "vue-i18n";
import enTranslations from "@/i18n/locales/en.json";
import heTranslations from "@/i18n/locales/he.json";
import arTranslations from "@/i18n/locales/ar.json";

// Type definition for translation objects
type TranslationObject = {
  [key: string]: string | TranslationObject;
};

// Translation files
const translationFiles = {
  en: enTranslations,
  he: heTranslations,
  ar: arTranslations
};

export default defineComponent({
  name: "CustomFieldEditor",
  props: {
    field: {
      type: Object as PropType<CustomField | null>,
      default: null,
    },
    visible: {
      type: Boolean,
      default: false,
    },
    editMode: {
      type: Boolean,
      default: false,
    },
    existingKeys: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
  },
  emits: ["close", "save"],
  setup(props, { emit }) {
    const { locale } = useI18n();
    
    // Direct translation function
    const directT = (key: string, params: Record<string, any> = {}): string => {
      try {
        // Split the key into parts (e.g., "settings.leadsSettings.customFields.title" -> ["settings.leadsSettings", "customFields", "title"])
        const parts = key.split('.');
        
        // Start with the current locale's translations
        let currentObj: any = translationFiles[locale.value as keyof typeof translationFiles];
        
        // Navigate through the nested objects
        for (let i = 0; i < parts.length; i++) {
          const part = parts[i];
          
          // If we can't go deeper, break
          if (!currentObj || typeof currentObj !== 'object' || !(part in currentObj)) {
            // Try English fallback
            currentObj = translationFiles.en;
            
            // Navigate through the English translations
            for (let j = 0; j <= i; j++) {
              if (!currentObj || typeof currentObj !== 'object' || !(parts[j] in currentObj)) {
                return key; // Return the key if not found in English either
              }
              currentObj = currentObj[parts[j] as keyof typeof currentObj];
            }
            
            // Continue with the remaining parts using English
            for (let j = i + 1; j < parts.length; j++) {
              if (!currentObj || typeof currentObj !== 'object' || !(parts[j] in currentObj)) {
                return key; // Return the key if not found
              }
              currentObj = currentObj[parts[j] as keyof typeof currentObj];
            }
            
            break;
          }
          
          // Move to the next level
          currentObj = currentObj[part as keyof typeof currentObj];
        }
        
        // At this point, currentObj should be the translation string
        let result = typeof currentObj === 'string' ? currentObj : key;
        
        // Replace parameters in the string if any
        if (params && Object.keys(params).length > 0) {
          Object.entries(params).forEach(([paramKey, paramValue]) => {
            result = result.replace(new RegExp(`{${paramKey}}`, 'g'), String(paramValue));
          });
        }
        
        return result;
      } catch (error) {
        console.error(`Translation error for key: ${key}`, error);
        return key; // Return the key if any error occurs
      }
    };
    
    const fieldData = ref<CustomField>({
      id: "",
      key: "",
      label: "",
      type: "text",
      required: false,
      options: [],
    });

    const optionsText = ref("");
    const isEditMode = computed(() => props.editMode);

    // Reset the form to default values
    const resetForm = () => {
      fieldData.value = {
        id: "",
        key: "",
        label: "",
        type: "text",
        required: false,
        options: [],
      };
      optionsText.value = "";
    };

    // When field prop changes, update the local fieldData
    watch(
      () => props.field,
      (newField) => {
        if (newField) {
          fieldData.value = { ...newField };
          if (newField.options && newField.options.length > 0) {
            optionsText.value = newField.options.join("\n");
          } else {
            optionsText.value = "";
          }
        } else {
          resetForm();
        }
      },
      { immediate: true }
    );

    // Generate a field key from the label
    const generateFieldKey = () => {
      if (!fieldData.value.key || fieldData.value.key === "") {
        let key = fieldData.value.label
          .toLowerCase()
          .replace(/[^a-z0-9]/g, "_")
          .replace(/_+/g, "_")
          .replace(/^_|_$/g, "");

        // Ensure the key is unique
        if (props.existingKeys.includes(key) && !isEditMode.value) {
          let count = 1;
          let newKey = `${key}_${count}`;
          while (props.existingKeys.includes(newKey)) {
            count++;
            newKey = `${key}_${count}`;
          }
          key = newKey;
        }

        fieldData.value.key = key;
      }
    };

    // Save the field
    const saveField = () => {
      // Process options from textarea if field type is select
      if (fieldData.value.type === "select" && optionsText.value) {
        fieldData.value.options = optionsText.value
          .split("\n")
          .map((option) => option.trim())
          .filter((option) => option !== "");
      } else {
        fieldData.value.options = [];
      }

      generateFieldKey();
      emit("save", { ...fieldData.value });
      close();
    };

    // Close the modal
    const close = () => {
      emit("close");
      setTimeout(resetForm, 300); // Reset after animation completes
    };

    return {
      fieldData,
      optionsText,
      isEditMode,
      saveField,
      close,
      generateFieldKey,
      directT
    };
  },
});
</script> 